/**
 * Module-level registry bridging the CRM preview context across React
 * subtree boundaries.
 *
 * Why it exists: `ProposalPreviewProvider` mounts INSIDE the proposal detail
 * surfaces (`ProposalDetailPanel`, `InlineProposalExpanded`), but the record
 * slideout renders OUTSIDE those subtrees — `SlideoutRenderer` lives at the
 * app root (`pages/Root.tsx`), and the action detail page hosts
 * `InlineCrmObjectPanel` as a `SplitView` sibling of the detail panel. A pure
 * context read (`useCrmPreview()`) is therefore null in the slideout, and the
 * projection wiring in `record-slideout-content.tsx` never activates in
 * production, in either AI-actions shell (legacy `ProposalCenter` or the
 * `ai_actions_redesign` pages).
 *
 * `CrmPreviewProvider` registers its handle here for its mount lifetime.
 * Consumers reach it through `useActivePreviewHandle` (CrmPreviewContext.tsx):
 * context when inside a provider subtree, else the registered handle.
 * Deliberate semantics (widening the original Phase-1 context-only scoping):
 * while a preview is active — a proposal surface is mounted with overlays —
 * projected CRM state resolves everywhere it renders, including the
 * out-of-subtree slideout and the store read hooks inside it. With no
 * provider mounted, `getActivePreview()` is null and every consumer is
 * byte-for-byte at baseline.
 *
 * Last-mounted-wins: when several providers are mounted at once, the most
 * recently registered handle is active; unregistration removes an entry
 * wherever it sits in the stack, so out-of-order unmounts are safe.
 */
import type { CrmPreviewHandle } from './types';

const stack: CrmPreviewHandle[] = [];
const registryListeners = new Set<() => void>();

function notifyRegistryChanged(): void {
  // Copy before iterating: a listener may unsubscribe (or re-subscribe)
  // synchronously while we notify.
  for (const cb of [...registryListeners]) {
    cb();
  }
}

/**
 * Registers a preview handle as the active one for its mount lifetime.
 * Returns an idempotent unregister function.
 */
export function registerActivePreview(handle: CrmPreviewHandle): () => void {
  stack.push(handle);
  notifyRegistryChanged();
  let unregistered = false;
  return () => {
    if (unregistered) {
      return;
    }
    unregistered = true;
    const index = stack.lastIndexOf(handle);
    if (index !== -1) {
      stack.splice(index, 1);
      notifyRegistryChanged();
    }
  };
}

/** The most recently registered (still-mounted) preview handle, if any. */
export function getActivePreview(): CrmPreviewHandle | null {
  return stack.length > 0 ? stack[stack.length - 1] : null;
}

/** Notifies `cb` whenever the active handle may have changed. */
export function subscribeToActivePreview(cb: () => void): () => void {
  registryListeners.add(cb);
  return () => {
    registryListeners.delete(cb);
  };
}

/** Test-only: empties the stack and listener set between test files. */
export function __resetActivePreviewRegistryForTesting(): void {
  stack.length = 0;
  registryListeners.clear();
}
